/**
 * Bridge between Profile types (saved gear) and Build type (trajectory inputs).
 *
 * Used by the visualizer to:
 *   - Load a saved bow + arrow profile into a Build for trajectory math
 *   - Save the current Build picker state as a new arrow profile (future)
 */

import type { Build, PointType, VaneGeometry } from '../lib/trajectory/types';
import type { BowProfile, ArrowProfile } from './types';
import { getArrow } from '../lib/arrowLibrary';

/**
 * Compose a Build from a saved bow profile + saved arrow profile.
 * Falls back to sensible defaults when fields aren't specified.
 */
export function buildFromProfiles(bow: BowProfile, arrow: ArrowProfile): Build {
  const totalGrains = computeTotalArrowGrains(arrow);
  const vaneGeometry = normalizeVaneGeometry(arrow.vanes.brand, arrow.vanes.model);

  return {
    arrowGrains: totalGrains,
    arrowLengthIn: arrow.shaft.lengthIn,
    spine: arrow.shaft.spine,
    fOCPercent: estimateFOCPercent(arrow, totalGrains),
    pointGrains: arrow.point.weightGr,
    pointType: arrow.point.type as PointType,
    insertGrains: arrow.insert.weightGr,
    vaneGeometry,
    vaneOffsetDeg: arrow.vanes.offsetDeg,
    vaneCount: arrow.vanes.count,
    bowIboFps: bow.bow.iboFps,
    bowDrawWeightLb: bow.bow.drawWeightLb,
    bowDrawLengthIn: bow.bow.drawLengthIn,
    measuredVelocityFps: bow.bow.measuredVelocityFps,
    tuneQualityYawDeg: 2,
    tuneQualitySource: 'default',
  };
}

function computeTotalArrowGrains(arrow: ArrowProfile): number {
  /* Try to look up the shaft in the arrow library to get a per-inch
   * grain figure. Fall back to a reasonable per-inch estimate (8 gpi) if
   * the shaft isn't in the library. */
  const VANES_APPROX_GR = 6;
  let shaftGpi = 8.0;
  if (arrow.shaft.brand && arrow.shaft.model) {
    const lib = getArrow(arrow.shaft.brand, arrow.shaft.model);
    if (lib) {
      const spineMatch = lib.spines.find((s) => s.spine === arrow.shaft.spine);
      if (spineMatch) shaftGpi = spineMatch.gpi;
    }
  }
  const shaftGrains = shaftGpi * arrow.shaft.lengthIn;
  return Math.round(
    shaftGrains +
      arrow.nock.weightGr +
      arrow.vanes.count * VANES_APPROX_GR +
      arrow.insert.weightGr +
      arrow.point.weightGr
  );
}

function estimateFOCPercent(arrow: ArrowProfile, totalGrains: number): number {
  /* Approximate FOC: point weight / total mass × a geometry factor.
   * For modern hunting arrows the rough relationship is:
   *
   *   FOC% ≈ ((pointGr + insertGr) / total - 0.10) × 100
   *
   * Calibrated so that 100gr point + 50gr insert + 460gr total ≈ 12.6% FOC. */
  const front = arrow.point.weightGr + arrow.insert.weightGr;
  const ratio = front / totalGrains;
  const foc = (ratio - 0.10) * 100;
  return Math.max(0, Math.min(25, Math.round(foc * 10) / 10));
}

function normalizeVaneGeometry(brand?: string, model?: string): VaneGeometry {
  const combined = `${brand ?? ''} ${model ?? ''}`.toLowerCase();
  if (combined.includes('dca') || combined.includes('sabre')) return 'DCA Sabre';
  if (combined.includes('aerovane')) return 'Aerovane 2.2';
  if (combined.includes('talon')) return 'AAE Talon';
  if (combined.includes('max stealth') || combined.includes('aae max')) return 'AAE Max Stealth';
  if (combined.includes('blazer') || combined.includes('bohning')) return 'Bohning Blazer';
  return 'Other';
}
